import React, { useState, useRef, useEffect } from 'react';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { useT } from './LocaleContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlobalStyle, DropdownItem, MenuButton, DropdownMenu } from './styles/GlobalStyles';
import { useOutsideClick, validatePassword, validateEmail, getErrorMessage, formatUserMessage } from './common';
import { authAPI } from './api';
import { 
  ResetPasswordContainer, 
  Title, 
  Form, 
  Input, 
  Button, 
  Message,
  PasswordRequirements,
  PasswordInputContainer,
  ResendContainer,
  ResendButton,
  CountdownText
} from './styles/ResetPasswordPageStyles';


const ResetPasswordPage = () => {
  const t = useT();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const countdownRef = useRef(null);

  // Получаем email из state при редиректе с ForgotPasswordPage
  useEffect(() => {
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
      // Запускаем обратный отсчет при переходе с ForgotPasswordPage
      setCountdown(30);
    }
  }, [location.state]);

  // Обратный отсчет
  useEffect(() => {
    if (countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [countdown]);

  useOutsideClick(dropdownRef, () => setMenuOpen(false));
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !code) {
      setMessage({ text: t('resetPassword.enterEmailAndCode'), success: false });
      return;
    }

    if (!password || !confirmPassword) {
      setMessage({ text: t('resetPassword.enterNewPassword'), success: false });
      return;
    }

    const emailValidation = validateEmail(email.trim());
    if (!emailValidation.valid) {
      setMessage({ text: emailValidation.message, success: false });
      return;
    }
    
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setMessage({ text: passwordValidation.message, success: false });
      return;
    }
    
    if (password !== confirmPassword) {
      setMessage({ text: t('resetPassword.passwordsMismatch'), success: false });
      return;
    }
    try {
      const response = await authAPI.passwordReset({
        email: email.trim(),
        code: code.trim(),
        password,
      });
      const msg = response.data.msg || t('resetPassword.updated');
      setMessage({ text: formatUserMessage(msg), success: true });
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('common.unknownError'));
      setMessage({
        text: t('common.errorPrefix', { message: errorMessage }),
        success: false,
      });
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || isResending || !email) return;
    
    setIsResending(true);
    try {
      const response = await authAPI.passwordForgot(email.trim());
      const msg = response.data.msg || t('forgotPassword.codeSent');
      setMessage({ 
        text: formatUserMessage(msg), 
        success: true 
      });
      setCountdown(30); // Запускаем обратный отсчет заново
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('common.unknownError'));
      setMessage({
        text: t('common.errorPrefix', { message: errorMessage }),
        success: false,
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <ResetPasswordContainer>
      <GlobalStyle />
      {message && <Message success={message.success}>{message.text}</Message>}
      <MenuButton onClick={toggleMenu} $open={menuOpen}>≡</MenuButton>
      <DropdownMenu ref={dropdownRef} open={menuOpen}>
        <DropdownItem to="/">{t('nav.home')}</DropdownItem>
        <ThemeToggle />
        <LanguageToggle />
      </DropdownMenu>

      <Title>{t('resetPassword.title')}</Title>
      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder={t('common.email')}
          value={email}
          maxLength={254}
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value.trim())}
          disabled={!!location.state?.email}
          style={{ marginBottom: '10px' }}
        />
        <Input
          type="text"
          placeholder={t('common.code')}
          value={code}
          maxLength={10}
          inputMode="numeric"
          onChange={(e) => setCode(e.target.value.trim())}
          $narrow
          style={{ marginBottom: '10px' }}
        />
        {email && (
          <ResendContainer>
            {countdown > 0 ? (
              <CountdownText>
                {t('resetPassword.resendIn', { seconds: countdown })}
              </CountdownText>
            ) : (
              <CountdownText>
                {t('resetPassword.didNotReceive')}
              </CountdownText>
            )}
            <ResendButton
              type="button"
              onClick={handleResend} 
              disabled={countdown > 0 || isResending}
            >
              {isResending ? t('resetPassword.sending') : t('resetPassword.resend')}
            </ResendButton>
          </ResendContainer>
        )}
        <PasswordInputContainer spacing="8px">
          <PasswordRequirements>
            {t('register.passwordHint')}
          </PasswordRequirements>
          <Input
            type="password"
            placeholder={t('resetPassword.newPassword')}
            value={password}
            maxLength={128}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </PasswordInputContainer>
        <Input
          type="password"
          placeholder={t('common.confirmPassword')}
          value={confirmPassword}
          maxLength={128}
          autoComplete="new-password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && email && code && password && confirmPassword) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <Button type="submit" disabled={!email || !code}>{t('common.submit')}</Button>
      </Form>
    </ResetPasswordContainer>
  );
};

export default ResetPasswordPage;
